/**
 * Template Selector Component
 * Allows selection of survey templates based on program level
 */

import React, { useState, useEffect } from 'react';
import { FileText, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const TemplateSelector = ({ 
  programLevel, 
  selectedTemplateId, 
  onSelect, 
  disabled = false 
}) => {
  const { getAccessToken } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load templates when component mounts or program level changes
  useEffect(() => {
    loadTemplates();
  }, [programLevel]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = await getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL;
      
      // Build query params
      const params = new URLSearchParams({
        active_only: 'true'
      });
      
      if (programLevel) {
        params.append('program_level', programLevel);
      }

      const url = `${apiUrl}/leading/api/survey-templates?${params.toString()}`;
      console.log('[TemplateSelector] Fetching templates from:', url);
      console.log('[TemplateSelector] Program level:', programLevel);
      console.log('[TemplateSelector] Access token present:', !!accessToken);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[TemplateSelector] Response status:', response.status);
      console.log('[TemplateSelector] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[TemplateSelector] Error response:', errorText);
        throw new Error(`Failed to load templates (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      console.log('[TemplateSelector] Response data:', data);
      
      const templatesList = data.data?.templates || data.templates || [];
      console.log('[TemplateSelector] Templates loaded:', templatesList.length);
      setTemplates(templatesList);

      // Auto-select first template if none selected and templates available
      if (!selectedTemplateId && templatesList.length > 0) {
        onSelect(templatesList[0].id);
      }
    } catch (err) {
      console.error('[TemplateSelector] Error loading templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = parseInt(e.target.value);
    onSelect(templateId);
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  // Helper to format program level display names
  const formatProgramLevel = (level) => {
    const labels = {
      'FRONTLINE_LEADERS': 'Frontline Leaders',
      'INFORMAL_LEADERS': 'Informal Leaders',
      'LEADERS_OF_LEADERS': 'Leaders of Leaders',
      'EXPERT_LEADERS': 'Expert Leaders',
      'VISIONARY_LEADERS': 'Visionary Leaders'
    };
    return labels[level] || level;
  };

  if (loading) {
    return (
      <div style={{
        padding: '1.5rem',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #10b981',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Loading templates...
          </span>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '1.5rem',
        background: '#fef2f2',
        border: '2px solid #dc2626',
        borderRadius: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <AlertCircle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
          <div>
            <p style={{ 
              color: '#991b1b', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              margin: '0 0 0.25rem 0'
            }}>
              Failed to load templates
            </p>
            <p style={{ color: '#991b1b', fontSize: '0.75rem', margin: 0 }}>
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div style={{
        padding: '1.5rem',
        background: '#fef3c7',
        border: '2px solid #d97706',
        borderRadius: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <AlertCircle size={20} color="#d97706" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
          <div>
            <p style={{ 
              color: '#78350f', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              margin: '0 0 0.25rem 0'
            }}>
              No templates available
            </p>
            <p style={{ color: '#78350f', fontSize: '0.75rem', margin: 0 }}>
              {programLevel 
                ? `No active templates found for ${formatProgramLevel(programLevel)}`
                : 'No active templates found. Please select a program level first.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: '0.875rem',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '0.5rem'
      }}>
        Survey Template *
      </label>
      
      <select
        value={selectedTemplateId || ''}
        onChange={handleTemplateChange}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '0.75rem',
          border: '1px solid #d1d5db',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          background: disabled ? '#f3f4f6' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: disabled ? '#9ca3af' : '#111827'
        }}
      >
        <option value="">Select a template...</option>
        {templates.map(template => (
          <option key={template.id} value={template.id}>
            {template.name} ({template.question_count} questions)
          </option>
        ))}
      </select>

      {/* Template Details */}
      {selectedTemplate && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '0.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <FileText size={20} color="#059669" style={{ flexShrink: 0, marginTop: '0.125rem' }} />
            <div style={{ flex: 1 }}>
              <h4 style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#065f46',
                margin: '0 0 0.5rem 0'
              }}>
                {selectedTemplate.name}
              </h4>
              {selectedTemplate.description && (
                <p style={{
                  fontSize: '0.75rem',
                  color: '#047857',
                  margin: '0 0 0.75rem 0',
                  lineHeight: '1.5'
                }}>
                  {selectedTemplate.description}
                </p>
              )}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '0.75rem',
                fontSize: '0.75rem'
              }}>
                <div>
                  <span style={{ color: '#047857', fontWeight: '600' }}>Program Level:</span>
                  <br />
                  <span style={{ color: '#065f46' }}>
                    {formatProgramLevel(selectedTemplate.program_level)}
                  </span>
                </div>
                <div>
                  <span style={{ color: '#047857', fontWeight: '600' }}>Questions:</span>
                  <br />
                  <span style={{ color: '#065f46' }}>
                    {selectedTemplate.question_count}
                  </span>
                </div>
                <div>
                  <span style={{ color: '#047857', fontWeight: '600' }}>Version:</span>
                  <br />
                  <span style={{ color: '#065f46' }}>
                    {selectedTemplate.version}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
